// Error handling - only show popups in development, log silently in production
// #region agent log - Runtime error capture (DISABLED IN PRODUCTION)
// Only enable debug logging in development mode to avoid CSP errors
const ENABLE_DEBUG_LOGGING = import.meta.env.DEV && import.meta.env.VITE_ENABLE_DEBUG_LOGGING === 'true';
const DEBUG_LOG_ENDPOINT = 'http://127.0.0.1:7242/ingest/e797c4c0-11c9-4ac7-98bf-adf19341742f';
const originalConsole = {
  log: console.log.bind(console),
  error: console.error.bind(console),
  warn: console.warn.bind(console),
  info: console.info.bind(console),
};

function sendDebugLog(level: string, message: string, data?: any) {
  // Only send debug logs in development if explicitly enabled
  if (!ENABLE_DEBUG_LOGGING) {
    return;
  }
  
  try {
    // Only try to send in development
    if (typeof fetch !== 'undefined' && import.meta.env.DEV) {
      fetch(DEBUG_LOG_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          location: 'main.tsx:runtime',
          message: `[${level}] ${message}`,
          data: data || {},
          timestamp: Date.now(),
          sessionId: 'runtime-debug',
          runId: 'live-page',
          hypothesisId: 'A'
        })
      }).catch(() => {}); // Silently fail if server not available
    }
  } catch (e) {
    // Silently fail
  }
}

// Only intercept console methods if debug logging is enabled
if (ENABLE_DEBUG_LOGGING) {
  console.log = (...args: any[]) => {
    originalConsole.log(...args);
    const message = args.map(a => typeof a === 'object' ? JSON.stringify(a) : String(a)).join(' ');
    sendDebugLog('LOG', message, { args });
  };

  console.error = (...args: any[]) => {
    originalConsole.error(...args);
    const message = args.map(a => typeof a === 'object' ? JSON.stringify(a) : String(a)).join(' ');
    sendDebugLog('ERROR', message, { args });
  };

  console.warn = (...args: any[]) => {
    originalConsole.warn(...args);
    const message = args.map(a => typeof a === 'object' ? JSON.stringify(a) : String(a)).join(' ');
    sendDebugLog('WARN', message, { args });
  };

  console.info = (...args: any[]) => {
    originalConsole.info(...args);
    const message = args.map(a => typeof a === 'object' ? JSON.stringify(a) : String(a)).join(' ');
    sendDebugLog('INFO', message, { args });
  };
}
// #endregion agent log
if (import.meta.env.DEV) {
  // Development: Show error popups for debugging
  window.onerror = (msg, url, line, col, error) => {
    console.error('GLOBAL ERROR:', msg, error);
    const errorDiv = document.createElement('div');
    errorDiv.style.cssText = 'position:fixed;top:0;left:0;right:0;background:red;color:white;padding:20px;z-index:99999;';
    errorDiv.innerHTML = `<h1>JS CRASHED: ${msg}</h1><pre>${error?.stack ?? ''}</pre>`;
    document.body.appendChild(errorDiv);
  };

  window.addEventListener('unhandledrejection', (event) => {
    console.error('UNHANDLED PROMISE REJECTION:', event.reason);
    const errorDiv = document.createElement('div');
    errorDiv.style.cssText = 'position:fixed;top:0;left:0;right:0;background:red;color:white;padding:20px;z-index:99999;';
    errorDiv.innerHTML = `<h1>UNHANDLED PROMISE REJECTION</h1><pre>${event.reason?.stack || String(event.reason)}</pre>`;
    document.body.appendChild(errorDiv);
  });
} else {
  // Production: Only log to console, don't show popups to users
  window.onerror = (msg, url, line, col, error) => {
    console.error('GLOBAL ERROR:', msg, error);
    // #region agent log (only in dev if enabled)
    if (ENABLE_DEBUG_LOGGING) {
      sendDebugLog('ERROR', 'window.onerror', { msg: String(msg), url, line, col, error: error?.message, stack: error?.stack });
    }
    // #endregion agent log
    // Don't show popup to users in production
  };

  window.addEventListener('unhandledrejection', (event) => {
    // Filter out MetaMask extension errors - these are common and not critical
    const errorMessage = event.reason?.message || String(event.reason || '');
    if (errorMessage.includes('MetaMask extension not found') || 
        errorMessage.includes('Failed to connect to MetaMask')) {
      // Silently ignore MetaMask extension errors
      if (import.meta.env.DEV) {
        console.warn('[Filtered] MetaMask extension error:', event.reason);
      }
      event.preventDefault(); // Prevent unhandled rejection
      return;
    }
    
    console.error('UNHANDLED PROMISE REJECTION:', event.reason);
    // #region agent log (only in dev if enabled)
    if (ENABLE_DEBUG_LOGGING) {
      const reason = event.reason?.message || String(event.reason || '');
      sendDebugLog('ERROR', 'unhandledrejection', { reason, stack: event.reason?.stack });
    }
    // #endregion agent log
    // Don't show popup to users in production
  });
}

import React from 'react';
import ReactDOM from 'react-dom/client';
import { InternetIdentityProvider } from './hooks/useInternetIdentity';
import App from './App';
import './index.css';

class GlobalErrorBoundary extends React.Component<
  React.PropsWithChildren<object>,
  { hasError: boolean; error?: Error }
> {
  constructor(props: React.PropsWithChildren<object>) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('React error boundary caught', error, errorInfo);
    // Log component stack to identify which component is causing the issue
    if (errorInfo.componentStack) {
      console.error('Component Stack:', errorInfo.componentStack);
    }
    // Prevent the error from crashing the entire app
    // The error boundary will show a fallback UI instead
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-black text-white p-8 text-center">
          <h1 className="text-3xl font-bold mb-4">Something went wrong.</h1>
          <p className="text-sm text-gray-400 mb-4">Check the console for details.</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90"
          >
            Refresh Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

ReactDOM.createRoot(document.getElementById('root')!).render(
    <GlobalErrorBoundary>
        <InternetIdentityProvider>
            <App />
        </InternetIdentityProvider>
    </GlobalErrorBoundary>
);
